import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { HERO_SLUG } from "../../data/content";
import GoldButton from "../GoldButton";
import Picture from "../Picture";

export default function Hero() {
    return (
        <section
            className="relative min-h-screen w-full overflow-hidden bg-[#0B0B0C]"
            data-testid="hero-section"
        >
            {/* Background image with slow Ken Burns */}
            <div aria-hidden="true" className="absolute inset-0 z-0 [&>picture]:block [&>picture]:h-full">
                <Picture
                    slug={HERO_SLUG}
                    alt="Luxury smart home interior at dusk — architectural light and integrated technology by Advanced Connected Technologies"
                    priority
                    sizes="100vw"
                    className="h-full w-full object-cover animate-ken-burns"
                    style={{ willChange: "transform" }}
                />
                {/* Layered dark gradient so text always reads, regardless of image luminance */}
                <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0C]/70 via-[#0B0B0C]/50 to-[#0B0B0C]" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C]/90 via-transparent to-transparent" />
            </div>

            {/* Content */}
            <div className="relative z-10 flex min-h-screen flex-col justify-center pt-36 pb-32 md:pb-40">
                <div className="shell">
                    <motion.div
                        suppressHydrationWarning
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            duration: 0.9,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                        className="max-w-3xl"
                    >
                        <p className="eyebrow mb-8" data-testid="hero-eyebrow">
                            Luxury Smart Home Integration
                        </p>

                        <h1
                            className="font-serif text-[44px] leading-[1.05] sm:text-6xl lg:text-[84px] text-[#F3EFE7] tracking-[-0.01em]"
                            data-testid="hero-headline"
                        >
                            Intelligence,
                            <br />
                            <span className="italic font-light text-[#F3EFE7]">
                                Woven Into the Architecture.
                            </span>
                        </h1>

                        <motion.p
                            suppressHydrationWarning
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.9,
                                delay: 0.25,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className="mt-8 max-w-xl text-base md:text-lg text-[#9A958A] leading-[1.85]"
                        >
                            A concierge integration studio for principal
                            residences — where light, sound, climate, and
                            security are composed with the same care the
                            architect gave the stone.
                        </motion.p>

                        <motion.div
                            suppressHydrationWarning
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.9,
                                delay: 0.45,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className="mt-12 flex flex-col sm:flex-row gap-4"
                        >
                            <GoldButton to="/contact" testId="hero-cta-consult">
                                Book a Consultation
                            </GoldButton>
                            <GoldButton
                                to="/services"
                                variant="outline"
                                testId="hero-cta-explore"
                            >
                                Explore Our Services
                            </GoldButton>
                        </motion.div>
                    </motion.div>
                </div>
            </div>

            {/* Scroll indicator */}
            <motion.div
                suppressHydrationWarning
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2, duration: 0.8 }}
                className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 text-[#C9A86A]"
                aria-hidden="true"
            >
                <span className="text-[10px] tracking-[0.4em] uppercase">
                    Scroll
                </span>
                <motion.span
                    suppressHydrationWarning
                    initial={{ y: 0 }}
                    animate={{ y: [0, 6, 0] }}
                    transition={{
                        duration: 2.4,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                >
                    <ChevronDown size={18} strokeWidth={1.2} />
                </motion.span>
            </motion.div>
        </section>
    );
}
